import { PAWAPAY_COUNTRIES } from "@reactive-resume/utils";
import type { Country } from "react-phone-number-input";
import {
  isPossiblePhoneNumber,
  isValidPhoneNumber,
  parsePhoneNumber,
} from "react-phone-number-input/max";
import frLabels from "react-phone-number-input/locale/fr.json";

/**
 * Libellés français, avec les deux Congo clairement séparés.
 * Sans ça, l’anglais du composant affiche « Congo » pour Brazzaville
 * et les utilisateurs de Kinshasa choisissent le mauvais indicatif (+242).
 */
export const MOMO_PHONE_LABELS: Record<string, string> = {
  ...frLabels,
  CG: "Congo-Brazzaville",
  CD: "RD Congo (Kinshasa)",
  CI: "Côte d'Ivoire",
  GN: "Guinée",
  GQ: "Guinée équatoriale",
};

export function momoCountryLabel(iso2: string): string {
  return MOMO_PHONE_LABELS[iso2] ?? iso2;
}

/**
 * Pays proposés pour le Mobile Money.
 *
 * Base = corridors PawaPay (priorité 0).
 * GW = Guinée-Bissau, couverte en UEMOA via PayDunya.
 */
export const MOMO_PHONE_COUNTRIES = Array.from(
  new Set<Country>([...(PAWAPAY_COUNTRIES as readonly Country[]), "GW"]),
).sort((left, right) => momoCountryLabel(left).localeCompare(momoCountryLabel(right), "fr"));

const ISO2_TO_ISO3: Record<string, string> = {
  AO: "AGO",
  BJ: "BEN",
  BF: "BFA",
  BI: "BDI",
  CM: "CMR",
  CF: "CAF",
  TD: "TCD",
  CG: "COG",
  CD: "COD",
  CI: "CIV",
  GQ: "GNQ",
  GA: "GAB",
  GM: "GMB",
  GH: "GHA",
  GN: "GIN",
  GW: "GNB",
  KE: "KEN",
  LS: "LSO",
  LR: "LBR",
  MG: "MDG",
  MW: "MWI",
  ML: "MLI",
  MR: "MRT",
  MZ: "MOZ",
  NA: "NAM",
  NE: "NER",
  NG: "NGA",
  RW: "RWA",
  SN: "SEN",
  SL: "SLE",
  SZ: "SWZ",
  TZ: "TZA",
  TG: "TGO",
  UG: "UGA",
  ZM: "ZMB",
  ZW: "ZWE",
};

/** Exemple national (sans l’indicatif) affiché dans le champ. */
const MOMO_PHONE_EXAMPLES: Partial<Record<Country, string>> = {
  CM: "6 50 12 34 56",
  CG: "06 123 45 67",
  CD: "81 234 56 78",
  CI: "07 01 23 45 67",
  SN: "77 123 45 67",
  GA: "06 12 34 56",
  GN: "621 12 34 56",
  GQ: "222 123 456",
  CF: "70 00 00 00",
  TD: "66 00 00 00",
  BJ: "90 00 00 00",
  BF: "70 00 00 00",
  ML: "70 00 00 00",
  TG: "90 00 00 00",
  NE: "90 00 00 00",
  MG: "32 12 345 67",
  KE: "712 345 678",
  NG: "802 123 4567",
  GH: "24 123 4567",
  RW: "788 123 456",
  UG: "712 345 678",
  TZ: "712 345 678",
  BI: "79 123 456",
  AO: "923 123 456",
  GW: "955 123 456",
  MZ: "82 123 4567",
  MW: "991 234 567",
  ZM: "96 123 4567",
  ZW: "71 234 5678",
  NA: "81 234 5678",
  LS: "5012 3456",
  LR: "770 123 456",
  SL: "76 123 456",
  GM: "301 2345",
  MR: "22 12 34 56",
};

export function iso2ToIso3(iso2: string | undefined): string | undefined {
  if (!iso2) {
    return undefined;
  }

  return ISO2_TO_ISO3[iso2.toUpperCase()];
}

export function isMomoCountry(iso2: string | undefined): iso2 is Country {
  return Boolean(iso2 && MOMO_PHONE_COUNTRIES.includes(iso2 as Country));
}

export function momoPhonePlaceholder(iso2: string | undefined): string {
  if (!iso2 || !isMomoCountry(iso2)) {
    return "Numéro Mobile Money";
  }

  const example = MOMO_PHONE_EXAMPLES[iso2];

  return example ? `Ex. ${example}` : "Numéro Mobile Money";
}

export function momoPhoneHint(iso2: string | undefined): string {
  if (!iso2 || !isMomoCountry(iso2)) {
    return "Choisissez le pays du compte Mobile Money, puis saisissez le numéro national.";
  }

  if (iso2 === "CG") {
    return "Congo-Brazzaville (+242) : 9 chiffres, souvent 06… — ce n’est pas le même réseau que Kinshasa.";
  }

  if (iso2 === "CD") {
    return "RD Congo / Kinshasa (+243) : 9 chiffres sans le 0 (ex. 81…, 97…, 99…).";
  }

  if (iso2 === "CI") {
    return "Côte d’Ivoire (+225) : 10 chiffres depuis la réforme (ex. 07 01 23 45 67).";
  }

  if (iso2 === "CM") {
    return "Cameroun (+237) : 9 chiffres commençant par 6, sans le 0.";
  }

  return `Saisissez le numéro national ${momoCountryLabel(iso2)}, sans retaper l’indicatif.`;
}

export type ResolvedMomoPhone = {
  e164: string;
  countryIso2: Country;
  countryIso3: string;
};

/**
 * Source de vérité à l’envoi : le numéro E.164 parsé, pas le pays figé du formulaire.
 * Évite d’envoyer CMR + un numéro congolais si le sélecteur n’a pas synchronisé.
 */
export function resolveMomoPhone(value: string | undefined): ResolvedMomoPhone | null {
  if (!value) {
    return null;
  }

  const parsed = parsePhoneNumber(value);

  if (!parsed?.country || !parsed.number || !isMomoCountry(parsed.country)) {
    return null;
  }

  const countryIso3 = iso2ToIso3(parsed.country);

  if (!countryIso3) {
    return null;
  }

  const e164 = parsed.number;
  const acceptable =
    isValidPhoneNumber(e164, parsed.country) || isPossiblePhoneNumber(e164, parsed.country);

  if (!acceptable) {
    return null;
  }

  return {
    e164,
    countryIso2: parsed.country,
    countryIso3,
  };
}
